import { useMemo } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parse, stringify } from "qs";

import { Action, Category } from "@/constants/events";
import { useEventTracker } from "@/hooks/useEventTracker";
import { scrollToTop } from "@/utils/scrollPosition";

const Dropdown = dynamic(() => import("@/components/botanic/Dropdown"), {
  ssr: true,
});

const SortDropdown = ({
  availableSorts,
  eventCategory,
  sortDisplayStrings,
  onToggleDropdown,
  sortsAsLinks,
}: {
  availableSorts: string[];
  eventCategory: Category;
  sortDisplayStrings: Record<string, string>;
  onToggleDropdown?: () => void;
  sortsAsLinks?: boolean;
}) => {
  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { publishEvent } = useEventTracker();

  const validAvailableSorts = useMemo(
    () =>
      availableSorts.filter((value) =>
        Object.keys(sortDisplayStrings).includes(value),
      ),
    [availableSorts, sortDisplayStrings],
  );

  if (validAvailableSorts.length === 0) {
    return <></>;
  }

  const sortValue = searchParams?.get("sort") || validAvailableSorts[0];
  const query = parse(searchParams?.toString() || "");
  const { sort: _oldSort, page: _oldPage, ...rest } = query;

  const handleClick = (value: string) => {
    // If we're loading these as buttons, we need to include the filters when we handle click
    if (!sortsAsLinks) {
      const sortSearchParams = new URLSearchParams(searchParams?.toString());
      sortSearchParams.set("sort", value);

      push(`${pathname}?${sortSearchParams.toString()}`);
    }

    scrollToTop("smooth");

    publishEvent({
      action: Action.click,
      category: eventCategory,
      label: sortDisplayStrings[value].toLowerCase(),
    });
  };

  return (
    <Dropdown
      data-testid="sort-dropdown"
      label="Sort by"
      variant="small"
      preselectedOption={String(sortValue)}
      onToggleDropdown={onToggleDropdown}
      options={validAvailableSorts}
      onItemRender={(option) => {
        const orderedParams = {
          sort: option,
          ...rest,
        };

        const isHighestRatedWithNoFilters =
          option === "highest-rated" && !query.filter && !query.sort;

        return {
          as: Link,
          children: sortDisplayStrings[option],
          "data-testid": `listing-sort-${option}`,
          href: `${pathname}?${stringify(orderedParams, { encode: false })}`,
          onClick: () => handleClick(option),
          rel: !isHighestRatedWithNoFilters ? "nofollow" : undefined,
        };
      }}
    />
  );
};

export default SortDropdown;
